/**
 * 首页（与 iOS Apps/Parent/Sources/Home/HomeView.swift 一致，设计稿 13）：
 * 孩子切换 + 平板状态、"现在"卡片、孩子做完待确认的事、快捷操作（发提醒 / 加积分 / 审批兑换）、最新的兑换申请、今天动态。
 * 调试：?dev_sheet=reminder / adjust / redemptions 自动打开对应弹窗。
 */
import { Bell, Gift, Star, UserPlus, type LucideIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { bestPresence, fetchAgenda, onDataChanged, select, shallowEqual, useCalendar, useCurrentChild, useDevSheet, useStore, type Child, type PresenceLevel } from "../../core";
import { AdjustPointsSheet } from "../../components/AdjustPointsSheet";
import { InstantReminderSheet } from "../../components/InstantReminderSheet";
import { CheckinsCard } from "../../components/CheckinsCard";
import { pendingRedemptionsOf, RedemptionButtons, RedemptionInfo, RedemptionsSheet } from "../../components/RedemptionsSheet";
import { Button, ChildSwitcher, EmptyHint, OnlineDot, Page } from "../../ui";
import { NowCard } from "./NowCard";
import { TodayFeed } from "./TodayFeed";
import "./home.css";

const PRESENCE_TEXT: Record<PresenceLevel, string> = {
  online: "平板在线",
  standby: "平板待机 · 定时提醒正常",
  offline: "平板离线",
};

export function HomePage() {
  const child = useCurrentChild();
  const cal = useCalendar();
  const today = cal.today();
  const childId = child?.id ?? "";
  const [showReminder, setShowReminder] = useDevSheet("reminder");
  const [showAdjust, setShowAdjust] = useDevSheet("adjust");
  const [showRedemptions, setShowRedemptions] = useDevSheet("redemptions");
  const pendingAll = useStore((s) => pendingRedemptionsOf(s).length);

  // 今天的日程展开：进入页面拉一次，之后每次同步到新数据再拉
  useEffect(() => {
    if (!childId) return;
    void fetchAgenda(childId, today);
    return onDataChanged(() => void fetchAgenda(childId, today));
  }, [childId, today]);

  return (
    <Page gap={16}>
      <header className="home-header">
        <ChildSwitcher large />
        {child && <TabletStatus childId={child.id} />}
      </header>
      {child ? (
        <div className="home-grid">
          <div className="home-col">
            <NowCard child={child} />
            <CheckinsCard childId={child.id} />
            <div className="home-actions">
              <QuickAction title="发提醒" icon={Bell} tone="reminder" onClick={() => setShowReminder(true)} />
              <QuickAction title="加积分" icon={Star} tone="points" onClick={() => setShowAdjust(true)} />
              <QuickAction title="审批兑换" icon={Gift} tone="redeem" badge={pendingAll} onClick={() => setShowRedemptions(true)} />
            </div>
            <LatestRedemption child={child} />
          </div>
          <div className="home-col">
            <TodayFeed child={child} />
          </div>
        </div>
      ) : (
        <NoChild />
      )}
      {child && (
        <>
          <InstantReminderSheet open={showReminder} onClose={() => setShowReminder(false)} childId={child.id} />
          <AdjustPointsSheet open={showAdjust} onClose={() => setShowAdjust(false)} childId={child.id} />
        </>
      )}
      <RedemptionsSheet open={showRedemptions} onClose={() => setShowRedemptions(false)} />
    </Page>
  );
}

function TabletStatus({ childId }: { childId: string }) {
  const devices = useStore((s) => select.studentDevices(s, childId), shallowEqual);
  const onlineIds = useStore((s) => s.onlineDeviceIds);
  const level = bestPresence(devices, onlineIds);
  return <OnlineDot level={level ?? "offline"} text={level ? PRESENCE_TEXT[level] : "未绑定平板"} />;
}

function QuickAction({ title, icon: Icon, tone, badge = 0, onClick }: { title: string; icon: LucideIcon; tone: "reminder" | "points" | "redeem"; badge?: number; onClick: () => void }) {
  return (
    <button type="button" className={`home-action is-${tone}`} onClick={onClick}>
      <span className="home-action-icon">
        <Icon size={21} strokeWidth={2.4} />
      </span>
      <span>{title}</span>
      {badge > 0 && (
        <em className="kp-num" aria-label={`${badge} 个待审批`}>
          {badge}
        </em>
      )}
    </button>
  );
}

/** 当前孩子最新的一条待审批兑换申请 */
function LatestRedemption({ child }: { child: Child }) {
  const cal = useCalendar();
  const r = useStore((s) => pendingRedemptionsOf(s, child.id)[0]);
  if (!r) return null;
  return (
    <section className="home-card home-redemption" aria-label="兑换申请">
      <div className="home-card-head">
        <h2>兑换申请</h2>
        <span className="kp-num">{cal.timeString(r.requestedAt)}</span>
      </div>
      <RedemptionInfo redemption={r} balance={child.pointsBalance} />
      <RedemptionButtons redemption={r} />
    </section>
  );
}

function NoChild() {
  const navigate = useNavigate();
  return (
    <section className="home-card home-nochild">
      <EmptyHint icon={UserPlus} text="还没有添加孩子">
        <Button variant="dark" height={44} onClick={() => navigate("/me?add=child")}>
          去添加孩子
        </Button>
      </EmptyHint>
    </section>
  );
}
